import type { ChatState } from './interfaces.chat';
import { chatHistoryReducer } from './state.chat-history';
import { chatSessionReducer } from './state.chat-session';
import { chatSessionManagerReducer } from './state.chat-session-manager';
import { generateEmptyChatState } from './state.initial-state';
import { settingsReducer } from './state.settings';

function baseChatReducer(state: ChatState.State, action: ChatState.Action): ChatState.State {
  const newState = computeNewState(state, action);

  logAction(state, newState, action);

  return newState;
}

function batchActions(state: ChatState.State, { payload }: ChatState.BatchAction): ChatState.State {
  let updatedState = state;
  for (const action of payload.actions) {
    updatedState = baseChatReducer(updatedState, action);
  }

  return updatedState;
}

export function chatReducer(state: ChatState.State, action: ChatState.Action): ChatState.State {
  switch (action.type) {
    case 'batch-actions':
      return batchActions(state, action);

    default:
      return baseChatReducer(state, action);
  }
}

function computeNewState(state: ChatState.State, action: ChatState.Action): ChatState.State {
  switch (action.type) {
    case 'reset-chat-state':
      return generateEmptyChatState({
        contextItems: state.contextItems,
        context: action.payload.context,
        settings: state.settings,
        features: state.settings,
        brandIdentifier: action.payload.brandIdentifier,
        landingCardsVisible: state.session.landingCardsVisible,
      });

    default: {
      const newState: ChatState.State = {
        contextItems: state.contextItems,
        settings: settingsReducer(state.settings, action),
        history: chatHistoryReducer(state.history, action),
        session: chatSessionReducer(state, action),
        sessionManager: chatSessionManagerReducer(state.sessionManager, action),
        unsavedTabSnapshots: state.unsavedTabSnapshots,
      };

      newState.history = {
        ...newState.history,
        tabs: syncSessionTab(newState),
      };

      newState.unsavedTabSnapshots = syncUnsavedTabSnapshots(state, newState);

      return newState;
    }
  }
}

function syncSessionTab(newState: ChatState.State): ChatState.State['history']['tabs'] {
  const tabs = newState.history.tabs;
  const sessionIdentifier = newState.session.chatInstance.identifier;

  if (tabs.items.includes(sessionIdentifier)) {
    return tabs;
  }

  return {
    items: tabs.items.concat(sessionIdentifier),
    index: tabs.items.length,
  };
}

function syncUnsavedTabSnapshots(
  prevState: ChatState.State,
  newState: ChatState.State
): ChatState.State['unsavedTabSnapshots'] {
  const nextSnapshots: ChatState.State['unsavedTabSnapshots'] = {
    ...prevState.unsavedTabSnapshots,
  };
  const activeSession = newState.session;
  const activeIdentifier = activeSession.chatInstance.identifier;

  if (activeSession.chatInstance.id == null && newState.history.tabs.items.includes(activeIdentifier)) {
    nextSnapshots[activeIdentifier] = {
      chatInstance: activeSession.chatInstance,
      filters: activeSession.filters,
      landingCardsVisible: activeSession.landingCardsVisible,
    };
  }

  const tabIdentifiers = new Set(newState.history.tabs.items);

  for (const identifier of Object.keys(nextSnapshots)) {
    if (!tabIdentifiers.has(identifier)) {
      delete nextSnapshots[identifier];
      continue;
    }

    if (isPersistedTabIdentifier(newState, identifier)) {
      delete nextSnapshots[identifier];
    }
  }

  return nextSnapshots;
}

function isPersistedTabIdentifier(state: ChatState.State, identifier: string): boolean {
  if (state.session.chatInstance.identifier === identifier) {
    return state.session.chatInstance.id != null;
  }

  const historyMatch =
    state.history.items.find((item) => item.identifier === identifier) ||
    state.history.shared.find((item) => item.identifier === identifier);

  if (historyMatch) {
    return historyMatch.id != null;
  }

  const snapshot = state.unsavedTabSnapshots[identifier];
  return snapshot ? snapshot.chatInstance.id != null : false;
}

function logAction(oldState: ChatState.State, newState: ChatState.State, action: ChatState.Action) {
  // if (isDevEnv()) {
  //   console.groupCollapsed('Chat Action', action);
  //   console.log('Old State', oldState);
  //   console.log('New State', newState);
  //   console.groupEnd();
  // }
}
